import type { RoomView } from "../../shared/game";

// A snapshot sent before the host's request is processed is not a rejection.
// Keep feedback separate from names and authoritative server state.
export class RenamePromptFeedback {
    private roomCode: string | null = null;
    private pending = new Map<string, { name: string; expiresAt: number }>();

    constructor(private readonly timeoutMs: number) {}

    get nextExpiry(): number | null {
        return this.pending.size ? Math.min(...[...this.pending.values()].map(entry => entry.expiresAt)) : null;
    }

    request(room: RoomView, sessionId: string, now: number): RoomView {
        if (this.roomCode !== room.roomCode) this.pending.clear();
        this.roomCode = room.roomCode;
        const player = room.participants.find(entry => entry.sessionId === sessionId);
        if (!room.viewer.isHost || !player?.connected || player.isHost ||
            sessionId === room.viewer.sessionId || player.renamePending) return room;
        this.pending.set(sessionId, { name: player.name, expiresAt: now + this.timeoutMs });
        return this.patch(room, new Map([[sessionId, true]]));
    }

    receive(room: RoomView, now: number): RoomView {
        if (this.roomCode !== room.roomCode || !room.viewer.isHost) this.pending.clear();
        this.roomCode = room.roomCode;
        for (const [id, entry] of this.pending) {
            const player = room.participants.find(player => player.sessionId === id);
            if (!player || player.isHost || player.renamePending || player.name !== entry.name || now >= entry.expiresAt) {
                this.pending.delete(id);
            }
        }
        return this.patch(room, new Map([...this.pending.keys()].map(id => [id, true])));
    }

    expire(room: RoomView, now: number): RoomView {
        const expired = new Map<string, boolean>();
        for (const [id, entry] of this.pending) {
            if (now >= entry.expiresAt) {
                expired.set(id, false);
                this.pending.delete(id);
            }
        }
        return this.patch(room, expired);
    }

    clear(room: RoomView | null): RoomView | null {
        const cleared = new Map([...this.pending.keys()].map(id => [id, false]));
        this.pending.clear();
        this.roomCode = null;
        return room ? this.patch(room, cleared) : null;
    }

    private patch(room: RoomView, flags: Map<string, boolean>): RoomView {
        if (!flags.size) return room;
        const patchPlayers = <T extends { sessionId: string; renamePending?: boolean }>(players: T[]): T[] =>
            players.map(player => flags.has(player.sessionId)
                ? { ...player, renamePending: flags.get(player.sessionId)! } : player);
        return {
            ...room,
            participants: patchPlayers(room.participants),
            pictionary: room.pictionary ? { ...room.pictionary, teams: patchPlayers(room.pictionary.teams) } : null,
            vaultBreakers: room.vaultBreakers ? { ...room.vaultBreakers, teams: patchPlayers(room.vaultBreakers.teams) } : null
        };
    }
}
